import { CustomBlockMeta, CustomFieldMeta, CustomFieldTypes, CustomSectionMeta } from 'idea-toolbox';

/**
 * Get the list of answers to a choice, regardless of it accepting one or many answers.
 */
export const getAnswersToChoice = (value: string | string[]): string[] =>
  (Array.isArray(value) ? value : [value]).filter(a => a).map(a => String(a));

/**
 * The definition of a question of a form: a custom field that supports choices with multiple answers.
 */
export class FormFieldMeta extends CustomFieldMeta {
  /**
   * Whether the choice (type enum) accepts more than one answer; in that case, the answers are stored as an array.
   */
  multiple: boolean;

  load(x: any, languages?: any): void {
    super.load(x, languages);
    this.multiple = this.type === CustomFieldTypes.ENUM && Boolean(x.multiple);
  }

  fieldDefaultValue(): any {
    if (!this.multiple) return super.fieldDefaultValue();
    return getAnswersToChoice(this.default);
  }

  /**
   * Load an answer; a choice switched between single and multiple answers keeps (the first of) its answers.
   */
  loadField(newField: any): any {
    if (this.type !== CustomFieldTypes.ENUM) return super.loadField(newField);
    const answers = getAnswersToChoice(newField);
    return this.multiple ? answers : (answers[0] ?? null);
  }

  /**
   * The answers to a multiple choice must be among the possible ones; at least one, if the question is mandatory.
   */
  validateField(field: any): boolean {
    if (!this.multiple) return super.validateField(field);
    const answers = getAnswersToChoice(field);
    return answers.every(a => this.enum.includes(a)) && (!this.obligatory || !!answers.length);
  }
}

/**
 * The definition of a section of a form, made of questions.
 */
export class FormSectionMeta extends CustomSectionMeta {
  declare fields: { [fieldId: string]: FormFieldMeta };

  load(x: any, languages?: any): void {
    super.load(x, languages);
    this.fieldsLegend.forEach(f => (this.fields[f] = new FormFieldMeta(x.fields?.[f], languages)));
  }
}

/**
 * The definition of a form, made of sections.
 */
export class FormBlockMeta extends CustomBlockMeta {
  declare sections: { [sectionId: string]: FormSectionMeta };

  load(x: any, languages?: any): void {
    super.load(x, languages);
    this.sectionsLegend.forEach(s => (this.sections[s] = new FormSectionMeta(x.sections?.[s], languages)));
  }
}
